import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'wouter';

import { useTaxonomyUrlGenerator } from '../../hooks/useUrlGenerator';
import { isMessage } from '../../lib/utils';

import { typesSections } from '../../routes';
import PillsMenu from '../menus/PillsMenu';

import styles from '../../styles/menus/types-menu.module.css';

export type SortFilter = null | 'popular';

export interface TypesMenuProps {
    sort?: SortFilter;
    brand?: Brand;
    parent?: Section | Category | Topic | Format | null;
    sponsorised?: boolean;
    withSort?: boolean;
    withParent?: boolean;
    className?: string | null;
}

const TypesMenu = ({
    sort = null,
    brand = null,
    parent = null,
    sponsorised = false,
    withSort = false,
    withParent = false,
    className = null,
}: TypesMenuProps) => {
    const intl = useIntl();
    const url = useUrlGenerator();
    const [location] = useLocation();
    const taxonomyUrl = useTaxonomyUrlGenerator();
    const params = useParams();

    const { handle: brandHandle = null, slug: brandSlug = null } = brand || {};

    const types = typesSections
        .filter(({ id }) => id !== 'popular')
        .filter(({ subSection = false }) => !withParent || subSection === true)
        .filter(({ brands = null }) => brands === null || brands.indexOf(brandHandle) !== -1)
        .filter(({ sponso = false }) => !sponsorised || sponso === true)
        .map(({ id, path, label, all = false }) => ({
            id,
            strict: id === 'all',
            url:
                parent !== null
                    ? taxonomyUrl(
                          {
                              brand,
                              ...parent,
                          },
                          id !== 'all' || parent.type === 'section' ? path : null,
                      )
                    : sponsorised
                      ? url(
                            'sponso',
                            id === 'all'
                                ? {
                                      brand: brandSlug,
                                  }
                                : {
                                      brand: brandSlug,
                                      type: path,
                                  },
                        )
                      : url(all ? `${id}.all` : id, {
                            brand: brandSlug,
                        }),
            label: isMessage(label) ? intl.formatMessage(label) : label,
        }));

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                <div className={styles.group}>
                    <PillsMenu
                        items={types.map(({ id, strict = false, label, url: href }) => ({
                            id,
                            href,
                            label,
                            bordered: true,
                            branded: true,
                            active:
                                (strict && location === href) ||
                                (!strict && location.match(new RegExp(`^${href}`)) !== null) ||
                                (id === 'all' && location === url('popular', params)) ||
                                (id === 'all' &&
                                    parent !== null &&
                                    location ===
                                        taxonomyUrl({
                                            ...parent,
                                            brand,
                                        })),
                        }))}
                        itemsClassName={styles.items}
                        itemClassName={styles.item}
                        buttonClassName={styles.button}
                    />
                </div>
                {withSort ? (
                    <PillsMenu
                        items={[
                            {
                                id: 'recent',
                                href: location,
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Plus récents"
                                        description="Filter label"
                                    />
                                ),
                                bordered: true,
                                branded: true,
                                active: sort === null,
                            },
                            {
                                id: 'popular',
                                href: `${location}?sort=popular`,
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Plus populaires"
                                        description="Filter label"
                                    />
                                ),
                                bordered: true,
                                branded: true,
                                active: sort === 'popular',
                            },
                        ]}
                        itemsClassName={styles.items}
                        itemClassName={styles.item}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default TypesMenu;
